import { BASE_STATS, applyModifiers, xpForLevel, MAX_LEVEL, KEYSTONE_LEVELS, RANK_MAX, rankXpForRank, type Modifier } from '../content/stats';
import { HEROES, canEquipItem, type HeroId } from '../content/heroes';
import { SKILLS, aspectOf, aspectPath, resolveSkill, type SkillDef } from '../content/skills';
import { KEYSTONES } from '../content/keystones';
import { ITEMS, itemPerk, weaponAction, weaponDefOf, type ItemInst, type Rarity } from '../content/items';
import { CONSUMABLES } from '../content/consumables';
import { BLESSINGS, BLESSING_IDS } from '../content/blessings';
import type { Action, Flag, Hook } from '../content/actions';
import type { StatusId, Tag } from '../content/tags';
import { Rng } from '../rng';
import type { World } from './world';
import { TPS } from './world';
import { BACKPACK_SLOTS, EMPTY_INPUT, type DraftOffer, type DraftOption, type EntityId, type GhostSnapshot, type HeroSave, type InputState, type Interactable, type ItemSave, type KitSkillSave, type PerkSource, type PlayerEnt, type SkillSlot } from './types';
import { runAction, fireHooks, firePeriodicHooks } from './actions';
import { speedMul, isStunned, applyStatus } from './status';
import { heal } from './combat';
import { rollItem, spawnPickup, rollConsumable } from './loot';
import { PETS, petLevelMul } from '../content/pets';
import { spawnPet } from './mobs';
import { enforceBudget } from './build';

export interface CreatePlayerOpts {
  accountId: string;
  name: string;
  heroId: HeroId;
  team: number;
  x: number;
  y: number;
  seed: number;
  level?: number;
  pet?: { id: string; level: number } | null;
  /** a hero kept from an earlier realm; restores rank, kit and gear */
  save?: HeroSave | null;
}

export function playerPower(level: number): number {
  return 10 + 1.5 * (level - 1);
}

/** Shaman stances: the active attunement lends its element to every weapon hit. */
const ATTUNEMENTS: Partial<Record<StatusId, { tag: Tag; status: StatusId }>> = {
  attune_fire: { tag: 'fire', status: 'burn' },
  attune_frost: { tag: 'frost', status: 'chill' },
  attune_storm: { tag: 'lightning', status: 'shock' },
};

export function attunementOf(p: PlayerEnt): { tag: Tag; status: StatusId } | null {
  for (const s of p.statuses) {
    const a = ATTUNEMENTS[s.id];
    if (a) return a;
  }
  return null;
}

function attuneWeapon(a: Action, att: { tag: Tag; status: StatusId }): Action {
  if (a.type !== 'projectile' && a.type !== 'area') return a;
  return { ...a, tags: a.tags.includes(att.tag) ? a.tags : [...a.tags, att.tag], status: [...(a.status ?? []), { id: att.status, duration: 3, stacks: 1 }] };
}

const DASH_TICKS = 5;
const DASH_SPEED = 620;
const DASH_CD = 3;
/** enemies closer than this wake auto-cast actives */
const AUTOCAST_RANGE = 460;

export function createPlayer(world: World, o: CreatePlayerOpts): PlayerEnt {
  const hero = HEROES[o.heroId];
  const id = world.allocId();
  const weapon: ItemInst = { uid: world.allocId(), def: hero.weapon, rarity: 0, affixes: [] };
  const p: PlayerEnt = {
    id,
    kind: 'player',
    x: o.x,
    y: o.y,
    r: 14,
    team: o.team,
    facing: 0,
    hp: 1,
    maxHp: 1,
    stats: { ...BASE_STATS },
    statuses: [],
    shield: 0,
    dead: false,
    vx: 0,
    vy: 0,
    knockX: 0,
    knockY: 0,
    lastHitBy: 0,
    lastHurt: -999,
    name: o.name,
    level: o.level ?? 1,
    untargetable: false,
    accountId: o.accountId,
    heroId: o.heroId,
    xp: 0,
    xpNext: xpForLevel(o.level ?? 1),
    rank: 1,
    rankXp: 0,
    rankXpNext: rankXpForRank(1),
    blessings: [],
    backpack: [],
    crowdFedAt: -99999,
    input: { ...EMPTY_INPUT },
    lastSeq: 0,
    aim: 0,
    actives: [{ id: hero.startSkill, level: 1, aspects: [], cd: 0 }],
    autos: [],
    traits: [],
    keystones: [],
    equipment: { weapon },
    consumables: [null, null, null, null],
    draft: null,
    draftQueue: [],
    draftExtra: 0,
    weaponCd: 0,
    dashCd: 0,
    dashT: 0,
    dashDx: 0,
    dashDy: 0,
    dashCharges: 1,
    invulnUntil: world.tick + world.seconds(1.5),
    kills: 0,
    bossKills: 0,
    revenantKills: 0,
    damageDealt: 0,
    damageTaken: 0,
    summons: [],
    hookCds: new Map(),
    flags: new Set(),
    moving: false,
    spawnTick: world.tick,
    deathCause: null,
    lowHpArmed: true,
    power: playerPower(o.level ?? 1),
    seed: o.seed,
    fameEarned: 0,
    interactCd: 0,
    respawnAt: 0,
    perks: [],
    pet: null,
    carried: { kills: 0, bossKills: 0, revenantKills: 0, damageDealt: 0 },
  };
  world.players.set(id, p);
  if (o.save) restoreHero(world, p, o.save);
  if (o.pet && PETS[o.pet.id]) {
    const m = spawnPet(world, p, o.pet.id, o.pet.level);
    if (m) p.pet = { id: o.pet.id, level: o.pet.level, entityId: m.id };
  }
  recomputeStats(world, p);
  p.hp = p.maxHp;
  p.dashCharges = Math.floor(p.stats.dashCharges);
  return p;
}

export function removePlayer(world: World, id: EntityId): void {
  const p = world.players.get(id);
  if (!p) return;
  for (const sid of p.summons) {
    const s = world.mobs.get(sid);
    if (s) s.dead = true;
  }
  if (p.pet) {
    const pet = world.mobs.get(p.pet.entityId);
    if (pet) pet.dead = true;
  }
  world.players.delete(id);
}

export function cleanupDeadPlayers(world: World): void {
  for (const [id, p] of world.players) {
    if (p.dead && world.tick > p.respawnAt + world.seconds(120)) world.players.delete(id);
  }
}

/** Rebuilds stats, perks and flags from hero, traits, keystones, aspect paths, blessings and equipment. */
export function recomputeStats(world: World, p: PlayerEnt): void {
  const hero = HEROES[p.heroId];
  const mods: Modifier[] = [...hero.baseMods];
  const perks: PerkSource[] = [];
  const flags = new Set<Flag>();
  const addPerk = (key: string, src: { mods?: Modifier[]; hooks?: Hook[]; flags?: Flag[] } | undefined, skill?: string) => {
    if (!src) return;
    if (src.mods) mods.push(...src.mods);
    if (src.hooks && src.hooks.length) perks.push({ key, hooks: src.hooks, skill });
    if (src.flags) for (const f of src.flags) flags.add(f);
  };
  addPerk('hero', hero.trait);
  if (p.admin) mods.push(...p.admin.mods);
  for (const t of p.traits) {
    const def = SKILLS[t.id];
    if (!def) continue;
    addPerk('trait:' + t.id, def);
    if (def.modsPerLevel) for (let l = 1; l < t.level; l++) mods.push(...def.modsPerLevel);
  }
  for (const k of p.keystones) addPerk('key:' + k, KEYSTONES[k]);
  for (const slot of [...p.actives, ...p.autos]) {
    const def = SKILLS[slot.id];
    if (!def) continue;
    if (def.kind === 'auto') addPerk('auto:' + slot.id, { hooks: def.hooks, flags: def.flags }, slot.id);
    const res = resolveSkill(def, slot.aspects);
    res.perks.forEach((perk, i) => addPerk(`aspect:${slot.id}:${i}`, perk, slot.id));
  }
  p.blessings.forEach((id, i) => addPerk(`bless:${id}:${i}`, BLESSINGS[id]));
  const eq = p.equipment;
  addPerk('weapon', itemPerk(eq.weapon));
  if (eq.armor) addPerk('armor', itemPerk(eq.armor));
  if (eq.charm) addPerk('charm', itemPerk(eq.charm));
  if (p.pet) {
    const pdef = PETS[p.pet.id];
    if (pdef) {
      const mul = petLevelMul(p.pet.level);
      const scaled: Modifier[] = pdef.mods.map((m) => ({ stat: m.stat, add: m.add !== undefined ? m.add * mul : undefined, inc: m.inc !== undefined ? m.inc * mul : undefined, more: m.more }));
      addPerk('pet', { mods: scaled, hooks: pdef.hooks });
    }
  }
  // level scaling: small hp growth per level
  mods.push({ stat: 'maxHp', add: (p.level - 1) * 6 });
  const ratio = p.maxHp > 0 ? p.hp / p.maxHp : 1;
  p.stats = applyModifiers(BASE_STATS, mods);
  p.maxHp = p.stats.maxHp;
  p.hp = Math.max(1, Math.min(p.maxHp, Math.round(p.maxHp * ratio)));
  p.perks = perks;
  p.flags = flags;
  p.power = playerPower(p.level);
  if (p.dashCharges > p.stats.dashCharges) p.dashCharges = Math.floor(p.stats.dashCharges);
}

export function buildTags(p: PlayerEnt): Set<Tag> {
  const tags = new Set<Tag>();
  for (const s of [...p.actives, ...p.autos]) for (const t of SKILLS[s.id]?.tags ?? []) tags.add(t);
  for (const t of p.traits) for (const tg of SKILLS[t.id]?.tags ?? []) tags.add(tg);
  for (const k of p.keystones) for (const tg of KEYSTONES[k]?.synergy ?? []) tags.add(tg);
  for (const t of weaponDefOf(p.equipment.weapon.def).tags) tags.add(t);
  return tags;
}

export function stepPlayers(world: World): void {
  for (const p of world.players.values()) {
    if (p.dead) continue;
    stepPlayer(world, p);
  }
}

function scaledSkill(def: SkillDef, slot: SkillSlot): { action?: Action; mul: number; countBonus: number; radiusBonus: number; durationBonus: number; cooldown: number } {
  const res = resolveSkill(def, slot.aspects);
  const lvl = slot.level;
  const pl = def.perLevel ?? {};
  return {
    action: res.action,
    mul: 1 + (pl.damage ?? 0.2) * (lvl - 1),
    countBonus: (pl.count ?? 0) * (lvl - 1),
    radiusBonus: (pl.radius ?? 0) * (lvl - 1),
    durationBonus: (pl.duration ?? 0) * (lvl - 1),
    cooldown: (def.cooldown ?? 5) * res.cooldownMul * (1 + (pl.cooldown ?? 0) * (lvl - 1)),
  };
}

export function stepPlayer(world: World, p: PlayerEnt): void {
  const dt = world.dt;
  const inp = p.input;
  // timers
  if (p.weaponCd > 0) p.weaponCd--;
  if (p.interactCd > 0) p.interactCd--;
  for (const s of p.actives) if (s.cd > 0) s.cd--;
  const maxCharges = Math.max(1, Math.floor(p.stats.dashCharges));
  if (p.dashCharges < maxCharges) {
    if (p.dashCd > 0) p.dashCd--;
    if (p.dashCd <= 0) {
      p.dashCharges++;
      if (p.dashCharges < maxCharges) p.dashCd = Math.round(DASH_CD * p.stats.dashCooldown * TPS);
    }
  }
  // hp regen
  if (p.stats.hpRegen > 0 && p.hp < p.maxHp && world.tick % 20 === 0) heal(world, p, p.stats.hpRegen, null);
  if (world.tick % 20 === 0) firePeriodicHooks(world, p);

  const stunned = isStunned(p);
  // aim
  const ax = inp.ax - p.x;
  const ay = inp.ay - p.y;
  if (Math.abs(ax) + Math.abs(ay) > 1) p.aim = Math.atan2(ay, ax);

  // movement
  let mx = inp.mx;
  let my = inp.my;
  const ml = Math.hypot(mx, my);
  if (ml > 1) {
    mx /= ml;
    my /= ml;
  }
  const wantsMove = ml > 0.05 && !stunned;
  if (inp.dash && p.dashCharges > 0 && p.dashT <= 0 && !stunned) {
    p.dashT = DASH_TICKS;
    p.dashCharges--;
    if (p.dashCd <= 0) p.dashCd = Math.round(DASH_CD * p.stats.dashCooldown * TPS);
    if (wantsMove) {
      p.dashDx = mx;
      p.dashDy = my;
    } else {
      p.dashDx = Math.cos(p.aim);
      p.dashDy = Math.sin(p.aim);
    }
    p.invulnUntil = Math.max(p.invulnUntil, world.tick + DASH_TICKS);
    world.events.push({ t: 'fx', kind: 'dash', x: p.x, y: p.y, a: Math.atan2(p.dashDy, p.dashDx), team: p.team, id: p.id });
    fireHooks(world, p, 'dash', { x: p.x, y: p.y });
  }
  let vx = 0;
  let vy = 0;
  if (p.dashT > 0) {
    p.dashT--;
    vx = p.dashDx * DASH_SPEED;
    vy = p.dashDy * DASH_SPEED;
    p.moving = true;
  } else if (wantsMove) {
    const sp = p.stats.moveSpeed * speedMul(p);
    vx = mx * sp;
    vy = my * sp;
    p.moving = true;
  } else {
    p.moving = false;
  }
  p.vx = vx;
  p.vy = vy;
  const nx = p.x + (vx + p.knockX) * dt;
  const ny = p.y + (vy + p.knockY) * dt;
  p.knockX *= 0.78;
  p.knockY *= 0.78;
  const res = world.map.resolveCircle(nx, ny, p.r);
  p.x = res.x;
  p.y = res.y;
  if (p.moving && !inp.fire) p.facing = Math.atan2(vy, vx);
  if (inp.fire) p.facing = p.aim;

  if (stunned) {
    inp.skills = 0;
    inp.dash = false;
    return finishInput(world, p);
  }

  // weapon
  const canShoot = !(p.flags.has('noMoveShoot') && p.moving);
  if (inp.fire && p.weaponCd <= 0 && canShoot) {
    const wdef = weaponDefOf(p.equipment.weapon.def);
    const rarityMul = 1 + Math.min(2, p.equipment.weapon.rarity) * 0.1;
    let wact = weaponAction(p.equipment.weapon);
    const att = attunementOf(p);
    if (att) wact = attuneWeapon(wact, att);
    runAction(world, wact, { caster: p, x: p.x, y: p.y, aimX: inp.ax, aimY: inp.ay, power: p.power, mul: wdef.baseDamage * rarityMul, fromSkill: 'weapon' });
    p.weaponCd = Math.max(2, Math.round(TPS / (wdef.fireRate * p.stats.attackSpeed)));
  }

  // active skills: pressed this tick, or auto-cast at the nearest enemy
  let autoTarget: { x: number; y: number } | null | undefined;
  for (let i = 0; i < p.actives.length; i++) {
    const slot = p.actives[i];
    if (slot.cd > 0) continue;
    const pressed = (inp.skills & (1 << i)) !== 0;
    let aimX = inp.ax;
    let aimY = inp.ay;
    if (!pressed) {
      if (!slot.auto) continue;
      if (autoTarget === undefined) {
        const e = world.nearestEnemy(p.x, p.y, AUTOCAST_RANGE, p.team);
        autoTarget = e ? { x: e.x, y: e.y } : null;
      }
      if (!autoTarget) continue;
      aimX = autoTarget.x;
      aimY = autoTarget.y;
    }
    const def = SKILLS[slot.id];
    if (!def || !def.action) continue;
    const sc = scaledSkill(def, slot);
    if (!sc.action) continue;
    runAction(world, sc.action, { caster: p, x: p.x, y: p.y, aimX, aimY, power: p.power, mul: sc.mul, countBonus: sc.countBonus, radiusBonus: sc.radiusBonus, durationBonus: sc.durationBonus, fromSkill: slot.id });
    slot.cd = Math.max(2, Math.round(sc.cooldown * p.stats.cooldown * TPS));
    world.events.push({ t: 'cast', id: p.id, skill: slot.id, x: p.x, y: p.y });
    fireHooks(world, p, 'cast', { x: p.x, y: p.y, tags: def.tags, skill: slot.id });
    if (p.dead) return;
  }

  // auto skills
  for (const slot of p.autos) {
    const def = SKILLS[slot.id];
    if (!def || !def.action) continue;
    if (slot.cd > 0) {
      slot.cd--;
      continue;
    }
    const sc = scaledSkill(def, slot);
    if (!sc.action) continue;
    const aimX = p.x + Math.cos(p.aim) * 100;
    const aimY = p.y + Math.sin(p.aim) * 100;
    runAction(world, sc.action, { caster: p, x: p.x, y: p.y, aimX, aimY, power: p.power, mul: sc.mul, countBonus: sc.countBonus, radiusBonus: sc.radiusBonus, durationBonus: sc.durationBonus, fromSkill: slot.id });
    slot.cd = Math.max(4, Math.round(sc.cooldown * p.stats.cooldown * TPS));
    if (p.dead) return;
  }

  // consumables
  if (inp.use >= 0 && inp.use < 4) useConsumable(world, p, inp.use);

  // interact
  if (inp.interact && p.interactCd <= 0) {
    p.interactCd = TPS;
    let best: Interactable | null = null;
    let bestD = 80;
    for (const it of world.interactables.values()) {
      if (it.used) continue;
      const d = Math.hypot(it.x - p.x, it.y - p.y) - it.r;
      if (d < bestD) {
        bestD = d;
        best = it;
      }
    }
    if (best) useInteractable(world, p, best);
  }

  // draft
  if (inp.pick >= 0 && p.draft) applyPick(world, p, inp.pick);
  if (!p.draft) nextOffer(world, p);
  if (p.draft && world.tick >= p.draft.until) applyPick(world, p, 0);

  finishInput(world, p);
}

function finishInput(_world: World, p: PlayerEnt): void {
  const inp = p.input;
  // one-shot inputs are consumed
  inp.skills = 0;
  inp.dash = false;
  inp.use = -1;
  inp.pick = -1;
  inp.interact = false;
}

export function applyInput(p: PlayerEnt, i: InputState): void {
  const cur = p.input;
  cur.mx = i.mx;
  cur.my = i.my;
  cur.ax = i.ax;
  cur.ay = i.ay;
  cur.fire = i.fire;
  cur.skills |= i.skills;
  cur.dash = cur.dash || i.dash;
  if (i.use >= 0) cur.use = i.use;
  if (i.pick >= 0) cur.pick = i.pick;
  cur.interact = cur.interact || i.interact;
  cur.seq = i.seq;
  p.lastSeq = i.seq;
}

/** Experience feeds both tracks: the realm level (fast, burns) and the rank (slow, stays). */
export function addXp(world: World, p: PlayerEnt, amount: number): void {
  if (p.dead) return;
  const gained = Math.round(amount * p.stats.xpGain);
  addRankXp(world, p, gained);
  if (p.level >= MAX_LEVEL) return;
  p.xp += gained;
  while (p.xp >= p.xpNext && p.level < MAX_LEVEL) {
    p.xp -= p.xpNext;
    p.level++;
    p.xpNext = xpForLevel(p.level);
    p.draftQueue.push('skill');
    if (KEYSTONE_LEVELS.includes(p.level)) p.draftQueue.push('keystone');
    recomputeStats(world, p);
    heal(world, p, p.maxHp * 0.1, null);
    world.events.push({ t: 'levelup', id: p.id, level: p.level, x: p.x, y: p.y });
    fireHooks(world, p, 'levelup', { x: p.x, y: p.y });
    world.listeners.onLevelUp?.(p);
  }
}

export function addRankXp(world: World, p: PlayerEnt, amount: number): void {
  if (p.rank >= RANK_MAX || amount <= 0) return;
  p.rankXp += amount;
  while (p.rankXp >= p.rankXpNext && p.rank < RANK_MAX) {
    p.rankXp -= p.rankXpNext;
    p.rank++;
    p.rankXpNext = rankXpForRank(p.rank);
    world.events.push({ t: 'rankup', id: p.id, rank: p.rank, x: p.x, y: p.y });
    world.listeners.onRankUp?.(p);
  }
  if (p.rank >= RANK_MAX) p.rankXp = 0;
}

// ------------------------------------------------------------------ draft (realm level-ups)

function draftRng(p: PlayerEnt, world: World): Rng {
  return new Rng((p.seed ^ (world.tick * 2654435761)) >>> 0);
}

function blessingWeight(p: PlayerEnt, id: string, tags: Set<Tag>): number {
  const def = BLESSINGS[id];
  if (!def) return 0;
  const taken = p.blessings.filter((b) => b === id).length;
  if (taken >= def.maxStacks) return 0;
  const hero = HEROES[p.heroId];
  let tagW = 0;
  for (const t of def.tags) tagW += hero.tagWeights[t] ?? 1;
  let w = def.weight * (def.tags.length ? tagW / def.tags.length : 1);
  if (def.tags.some((t) => tags.has(t))) w *= 1.5;
  w *= def.rarity === 0 ? 1 : def.rarity === 1 ? 0.55 : 0.28;
  return w;
}

export function nextOffer(world: World, p: PlayerEnt): void {
  if (p.draft) return;
  const kind = p.draftQueue.shift();
  if (!kind) return;
  const rng = draftRng(p, world);
  const count = Math.min(5, 3 + p.draftExtra);
  const tags = buildTags(p);
  if (kind === 'keystone') {
    const owned = new Set(p.keystones);
    const excluded = new Set<string>();
    for (const k of p.keystones) for (const e of KEYSTONES[k]?.excludes ?? []) excluded.add(e);
    const cands = Object.values(KEYSTONES).filter((k) => !owned.has(k.id) && !excluded.has(k.id));
    const weights = cands.map((k) => 1 + (k.synergy.some((t) => tags.has(t)) ? 2 : 0));
    const options: DraftOption[] = [];
    while (options.length < 3 && cands.length) {
      const i = rng.weightedIndex(weights);
      options.push({ type: 'keystone', id: cands[i].id });
      cands.splice(i, 1);
      weights.splice(i, 1);
    }
    if (options.length) p.draft = { kind: 'keystone', options, until: world.tick + world.seconds(25) };
    else nextOffer(world, p);
    return;
  }
  // level-up cards: ranks of the hero's own skills and realm blessings, roughly half and half
  const upgrades: { opt: DraftOption; w: number }[] = [];
  for (const s of [...p.actives, ...p.autos]) {
    const def = SKILLS[s.id];
    if (!def || s.level >= def.maxLevel) continue;
    upgrades.push({ opt: { type: 'upgrade', id: s.id, skill: s.id, level: s.level + 1 }, w: 1 + (def.maxLevel - s.level) * 0.3 });
  }
  const blessings: { opt: DraftOption; w: number }[] = [];
  for (const id of BLESSING_IDS) {
    const w = blessingWeight(p, id, tags);
    if (w > 0) blessings.push({ opt: { type: 'blessing', id }, w });
  }
  const options: DraftOption[] = [];
  const draw = (pool: { opt: DraftOption; w: number }[]): boolean => {
    if (!pool.length) return false;
    const i = rng.weightedIndex(pool.map((c) => c.w));
    options.push(pool[i].opt);
    pool.splice(i, 1);
    return true;
  };
  const wantUpgrades = Math.min(upgrades.length, Math.ceil(count / 2));
  for (let i = 0; i < wantUpgrades; i++) draw(upgrades);
  while (options.length < count && (blessings.length || upgrades.length)) {
    if (!draw(blessings)) draw(upgrades);
  }
  p.draftExtra = 0;
  if (options.length) p.draft = { kind: 'skill', options, until: world.tick + world.seconds(25) };
}

export function applyPick(world: World, p: PlayerEnt, idx: number): void {
  const offer = p.draft;
  if (!offer) return;
  const opt = offer.options[Math.max(0, Math.min(offer.options.length - 1, idx))];
  p.draft = null;
  if (!opt) return;
  switch (opt.type) {
    case 'upgrade': {
      const def = SKILLS[opt.id];
      if (!def) break;
      const s = [...p.actives, ...p.autos].find((x) => x.id === opt.id);
      if (s) s.level = Math.min(def.maxLevel, s.level + 1);
      break;
    }
    case 'keystone':
      if (!p.keystones.includes(opt.id)) p.keystones.push(opt.id);
      break;
    case 'blessing':
      if (BLESSINGS[opt.id]) p.blessings.push(opt.id);
      break;
  }
  recomputeStats(world, p);
  world.events.push({ t: 'fx', kind: 'pick', x: p.x, y: p.y, team: p.team });
}

// ------------------------------------------------------------------ items & consumables

/**
 * Picks an item up: an empty slot is filled at once, a rarer piece replaces the worn one (which goes
 * to the backpack), anything else goes to the backpack. Returns false when nothing could take it.
 */
export function takeItem(world: World, p: PlayerEnt, item: ItemInst): boolean {
  const def = ITEMS[item.def];
  if (!def) return false;
  const slot = def.slot;
  // nothing is ever swapped out from under the hero: an empty slot is filled because that can only
  // help, everything else waits in the backpack until the player equips it deliberately
  if (!p.equipment[slot] && canEquipItem(p.heroId, item.def)) {
    if (slot === 'weapon') p.equipment.weapon = item;
    else p.equipment[slot] = item;
  } else if (p.backpack.length < BACKPACK_SLOTS) {
    p.backpack.push(item);
  } else return false;
  recomputeStats(world, p);
  world.events.push({ t: 'pickup', id: p.id, type: 'item', what: item.def, amount: item.rarity });
  if (item.rarity >= 3) world.events.push({ t: 'feed', key: 'feed.legendary', vars: { name: p.name, item: item.def } });
  return true;
}

/** @deprecated kept for callers that expect the old pickup semantics */
export function equipItem(world: World, p: PlayerEnt, item: ItemInst): void {
  takeItem(world, p, item);
}

export function addConsumable(p: PlayerEnt, id: string, n = 1): boolean {
  const def = CONSUMABLES[id];
  if (!def) return false;
  for (const c of p.consumables) {
    if (c && c.id === id && c.n < def.maxStack) {
      c.n = Math.min(def.maxStack, c.n + n);
      return true;
    }
  }
  for (let i = 0; i < p.consumables.length; i++) {
    if (!p.consumables[i]) {
      p.consumables[i] = { id, n: Math.min(def.maxStack, n) };
      return true;
    }
  }
  return false;
}

export function useConsumable(world: World, p: PlayerEnt, slot: number): void {
  const c = p.consumables[slot];
  if (!c) return;
  const def = CONSUMABLES[c.id];
  if (!def) return;
  const dbl = p.flags.has('consumablesDouble');
  if (def.special === 'draftExtra') {
    p.draftExtra = Math.min(2, p.draftExtra + (dbl ? 2 : 1));
  } else if (def.action) {
    runAction(world, def.action, { caster: p, x: p.x, y: p.y, aimX: p.input.ax, aimY: p.input.ay, power: p.power, mul: dbl ? 2 : 1, fromSkill: 'consumable:' + def.id });
    if (dbl && def.action.type === 'heal') runAction(world, def.action, { caster: p, x: p.x, y: p.y, aimX: p.input.ax, aimY: p.input.ay, power: p.power, mul: 1 });
  }
  c.n--;
  if (c.n <= 0) p.consumables[slot] = null;
  world.events.push({ t: 'pickup', id: p.id, type: 'use', what: def.id });
}

function useInteractable(world: World, p: PlayerEnt, it: Interactable): void {
  it.used = true;
  const rng = new Rng(it.seed);
  if (it.type === 'shrine') {
    heal(world, p, p.maxHp * 0.3, null);
    const buff = rng.pick(['might', 'haste', 'fortify', 'regen'] as const);
    applyStatus(world, p, { id: buff, duration: 20, stacks: buff === 'regen' ? 2 : 1 }, p, p.power);
    world.events.push({ t: 'fx', kind: 'shrine', x: it.x, y: it.y, r: 60, team: p.team });
  } else if (it.type === 'chest') {
    const legendary = it.tier >= 3 ? 0.04 + p.stats.luck * 0.005 : 0;
    const item = rollItem(rng, it.tier, p.stats.luck + 1, world.allocId(), 0, legendary, HEROES[p.heroId].weapons);
    spawnPickup(world, it.x + 20, it.y, { type: 'item', item, owner: p.id, ttl: 90 });
    spawnPickup(world, it.x - 20, it.y, { type: 'consumable', consumable: rollConsumable(rng), owner: p.id, ttl: 60 });
    if (p.flags.has('lootDouble')) spawnPickup(world, it.x, it.y + 20, { type: 'item', item: rollItem(rng, it.tier, p.stats.luck + 2, world.allocId(), 0, 0, HEROES[p.heroId].weapons), owner: p.id, ttl: 90 });
    world.events.push({ t: 'fx', kind: 'chest', x: it.x, y: it.y, r: 40, team: p.team });
  } else {
    // altar: sacrifice hp for a skill rank
    const cost = Math.round(p.hp * 0.25);
    if (p.hp - cost < 5) {
      it.used = false;
      return;
    }
    p.hp -= cost;
    world.events.push({ t: 'hit', id: p.id, x: p.x, y: p.y, dmg: cost, crit: false, tag: 'bleed', team: -1 });
    const upgradable = [...p.actives, ...p.autos].filter((s) => s.level < (SKILLS[s.id]?.maxLevel ?? 5));
    if (upgradable.length) {
      const s = rng.pick(upgradable);
      s.level++;
      recomputeStats(world, p);
    } else {
      applyStatus(world, p, { id: 'might', duration: 60 }, p, p.power);
    }
    world.events.push({ t: 'fx', kind: 'altar', x: it.x, y: it.y, r: 60, team: p.team });
  }
}

export function itemSave(it: ItemInst): ItemSave {
  return { def: it.def, rarity: it.rarity, affixes: it.affixes.map((a) => ({ ...a })) };
}

export function itemFromSave(world: World, s: ItemSave | undefined): ItemInst | undefined {
  if (!s || !ITEMS[s.def]) return undefined;
  const rarity = Math.max(0, Math.min(3, Math.floor(s.rarity))) as Rarity;
  return { uid: world.allocId(), def: s.def, rarity: ITEMS[s.def].legendary ? 3 : rarity, affixes: (s.affixes ?? []).map((a) => ({ ...a })) };
}

/** The most valuable worn piece; what a revenant may leave behind. */
export function bestItemOf(p: PlayerEnt): ItemInst | undefined {
  const worn = [p.equipment.weapon, p.equipment.armor, p.equipment.charm].filter((x): x is ItemInst => !!x);
  worn.sort((a, b) => b.rarity - a.rarity || b.affixes.length - a.affixes.length);
  return worn[0];
}

function kitSave(s: SkillSlot): KitSkillSave {
  const out: KitSkillSave = { id: s.id, aspects: [...s.aspects] };
  if (s.auto) out.auto = true;
  return out;
}

/** Snapshot of a living hero for the account slot: rank, kit and gear. Level, blessings and keystones stay in the realm. */
export function heroSaveFromPlayer(p: PlayerEnt, slot: number, prev: HeroSave | null, sessionSec: number): HeroSave {
  const now = Date.now();
  return {
    v: 2,
    slot,
    heroId: p.heroId,
    rank: p.rank,
    rankXp: p.rankXp,
    actives: p.actives.map(kitSave),
    autos: p.autos.map(kitSave),
    traits: p.traits.map((t) => ({ id: t.id, level: t.level })),
    equipment: { weapon: itemSave(p.equipment.weapon), armor: p.equipment.armor ? itemSave(p.equipment.armor) : undefined, charm: p.equipment.charm ? itemSave(p.equipment.charm) : undefined },
    backpack: p.backpack.map(itemSave),
    kills: p.kills,
    bossKills: p.bossKills,
    revenantKills: p.revenantKills,
    damageDealt: Math.round(p.damageDealt),
    runs: (prev?.runs ?? 0) + 1,
    playedSec: (prev?.playedSec ?? 0) + Math.max(0, Math.round(sessionSec)),
    createdAt: prev?.createdAt ?? now,
    updatedAt: now,
  };
}

/** Applies a saved hero onto a freshly created player; stats are rebuilt by the caller. */
export function restoreHero(world: World, p: PlayerEnt, raw: HeroSave): void {
  const s = normalizeHeroSave(raw);
  if (!s) return;
  p.heroId = HEROES[s.heroId] ? s.heroId : p.heroId;
  p.rank = Math.max(1, Math.min(RANK_MAX, Math.floor(s.rank)));
  p.rankXpNext = rankXpForRank(p.rank);
  p.rankXp = p.rank >= RANK_MAX ? 0 : Math.max(0, Math.min(p.rankXpNext - 1, Math.floor(s.rankXp)));
  const slot = (x: KitSkillSave): SkillSlot | null => (SKILLS[x.id] ? { id: x.id, level: 1, aspects: [...(x.aspects ?? [])], auto: x.auto || undefined, cd: 0 } : null);
  p.actives = s.actives.map(slot).filter((x): x is SkillSlot => !!x);
  p.autos = s.autos.map(slot).filter((x): x is SkillSlot => !!x);
  p.traits = s.traits.filter((t) => SKILLS[t.id]).map((t) => ({ id: t.id, level: Math.max(1, t.level) }));
  enforceBudget(p);
  const weapon = itemFromSave(world, s.equipment.weapon);
  p.equipment = { weapon: weapon ?? p.equipment.weapon, armor: itemFromSave(world, s.equipment.armor), charm: itemFromSave(world, s.equipment.charm) };
  p.backpack = s.backpack.map((it) => itemFromSave(world, it)).filter((x): x is ItemInst => !!x).slice(0, BACKPACK_SLOTS);
  p.kills = s.kills;
  p.bossKills = s.bossKills;
  p.revenantKills = s.revenantKills;
  p.damageDealt = s.damageDealt;
  p.carried = { kills: s.kills, bossKills: s.bossKills, revenantKills: s.revenantKills, damageDealt: s.damageDealt };
}

/**
 * Accepts a stored hero of any version and returns the current shape, or null for garbage.
 * Version-one heroes (level-based, aspects as a single id) become rank = old level with the same kit.
 */
export function normalizeHeroSave(raw: unknown): HeroSave | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.slot !== 'number' || typeof r.heroId !== 'string' || !HEROES[r.heroId as HeroId]) return null;
  const heroId = r.heroId as HeroId;
  const num = (v: unknown, d = 0): number => (typeof v === 'number' && Number.isFinite(v) ? v : d);
  const traits = Array.isArray(r.traits) ? (r.traits as { id: string; level: number }[]).filter((t) => t && typeof t.id === 'string').map((t) => ({ id: t.id, level: Math.max(1, num(t.level, 1)) })) : [];
  const eq = (r.equipment && typeof r.equipment === 'object' ? r.equipment : {}) as { weapon?: ItemSave; armor?: ItemSave; charm?: ItemSave };
  const equipment = { weapon: eq.weapon && typeof eq.weapon.def === 'string' ? eq.weapon : { def: HEROES[heroId].weapon, rarity: 0 as Rarity, affixes: [] }, armor: eq.armor, charm: eq.charm };
  const common = {
    slot: r.slot,
    heroId,
    traits,
    equipment,
    kills: num(r.kills),
    bossKills: num(r.bossKills),
    revenantKills: num(r.revenantKills),
    damageDealt: num(r.damageDealt),
    runs: num(r.runs),
    playedSec: num(r.playedSec),
    createdAt: num(r.createdAt, Date.now()),
    updatedAt: num(r.updatedAt, Date.now()),
  };
  const kit = (list: unknown): KitSkillSave[] =>
    Array.isArray(list)
      ? (list as Record<string, unknown>[])
          .filter((x) => x && typeof x.id === 'string' && SKILLS[x.id as string])
          .map((x) => {
            const id = x.id as string;
            let aspects: string[] = Array.isArray(x.aspects) ? (x.aspects as string[]).filter((a) => typeof a === 'string') : [];
            if (!aspects.length && typeof x.aspect === 'string' && aspectOf(id, x.aspect)) aspects = [x.aspect];
            aspects = aspectPath(id, aspects).map((a) => a.id);
            return { id, aspects, auto: x.auto === true || undefined };
          })
      : [];
  if (r.v === 2) {
    return { v: 2, ...common, rank: Math.max(1, Math.min(RANK_MAX, Math.floor(num(r.rank, 1)))), rankXp: Math.max(0, num(r.rankXp)), actives: kit(r.actives), autos: kit(r.autos), backpack: Array.isArray(r.backpack) ? (r.backpack as ItemSave[]).filter((it) => it && typeof it.def === 'string') : [] };
  }
  // legacy: the level was the only persistent track, so it becomes the rank
  const level = Math.max(1, Math.min(RANK_MAX, Math.floor(num(r.level, 1))));
  return { v: 2, ...common, rank: level, rankXp: 0, actives: kit(r.actives), autos: kit(r.autos), backpack: [] };
}

export function ghostFromPlayer(p: PlayerEnt, ghostId: string, survivedTicks: number): GhostSnapshot {
  const best = bestItemOf(p);
  return {
    id: ghostId,
    accountId: p.accountId,
    name: p.name,
    heroId: p.heroId,
    level: p.level,
    rank: p.rank,
    actives: p.actives.map((s) => ({ id: s.id, level: s.level, aspects: [...s.aspects] })),
    autos: p.autos.map((s) => ({ id: s.id, level: s.level, aspects: [...s.aspects] })),
    traits: p.traits.map((t) => ({ id: t.id, level: t.level })),
    keystones: [...p.keystones],
    weapon: p.equipment.weapon.def,
    kills: p.kills,
    survived: survivedTicks,
    item: best && best.rarity >= 1 ? itemSave(best) : undefined,
  };
}

export { scaledSkill };
